import React, { useState, useCallback } from 'react';
import PoDetailModal from './PoDetailModal';

/*
 * PoLink — drop-in clickable wrapper for a Purchase Order number anywhere
 * in the app. Renders the PO number (or any children) as a hyperlink-style
 * button; on click it opens the PoDetailModal in a portal-like overlay.
 *
 * Usage:
 *   <PoLink documentNumber="0000021677" />
 *   <PoLink documentNumber={row.purchase_document_num}>{row.short_label}</PoLink>
 *
 * Manages its own modal open state so any consumer just drops it in next to
 * a PO number and gets the document view for free. No props plumbing required.
 *
 * If a parent already manages a modal stack and wants to control open state,
 * use <PoDetailModal documentNumber={...} isOpen={...} onClose={...} /> directly.
 */
function PoLink({ documentNumber, departmentId, startDate, children, className = '', title }) {
  const [open, setOpen] = useState(false);
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (documentNumber) setOpen(true);
  }, [documentNumber]);
  const handleClose = useCallback(() => setOpen(false), []);

  if (!documentNumber) return <span className={className}>{children}</span>;

  return (
    <>
      <button
        type="button"
        className={`po-link ${className}`.trim()}
        onClick={handleClick}
        title={title || `Open PO ${documentNumber}`}
      >
        {children || documentNumber}
      </button>
      {open && (
        <PoDetailModal
          documentNumber={documentNumber}
          departmentId={departmentId}
          startDate={startDate}
          isOpen={open}
          onClose={handleClose}
        />
      )}
    </>
  );
}

export default PoLink;
